/**
 * AdminModules (T8.1a F3) — admin module catalog management page.
 *
 * Fetches GET /admin/modules?detail=full&include_inactive=true (D9 query params).
 * Query key: ['admin', 'modules', 'list'] (D11 — distinct from TenantModulesTab's 'catalog' key).
 * After mutation: invalidate ['admin', 'modules'] (parent — refreshes both list and catalog).
 *
 * "+ Dodaj moduł" button: disabled with i18n tooltip per ADR-0011 (deferred to F11+ plugin framework).
 *
 * Tabela = współdzielony `ConfigurableTable` (jak listy kartotek u tenanta) owinięty w
 * `PreviewTenantAuthBoundary` + `persistConfig={false}` — daje filtr/wyszukiwarkę pod każdą
 * kolumną (client-side na tablicy z react-query, zero zapytań). Obszar jest KOLUMNĄ
 * (filtrowalną) zamiast nagłówków grup — spłaszczenie listy pod filtrowanie.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Blocks } from 'lucide-react';

import { AdminRestoreModuleModal } from '@/components/AdminRestoreModuleModal';
import { AdminSoftDeleteModuleModal } from '@/components/AdminSoftDeleteModuleModal';
import { Badge, type BadgeColor } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { ConfigurableTable } from '@/components/configurable-table';
import type { ColumnDefinition } from '@/components/configurable-table/types';
import { EditModuleDrawer, type ModuleFull } from '@/components/EditModuleDrawer';
import { KartotekaBadge } from '@/components/KartotekaBadge';
import { Layout } from '@/components/Layout';
import { Skeleton } from '@/components/Skeleton';
import { PreviewTenantAuthBoundary } from '@/contexts/TenantAuthContext';
import { api } from '@/lib/api';
import type { KartotekaKind } from '@/lib/kartoteka-kind';

interface ModuleKartotekaItem {
  slug: string;
  name: string;
  /** Pochodzenie kartoteki — koloruje chip (systemowa=zielony, utworzona=pomarańczowy). */
  kind: KartotekaKind;
}

interface ModuleListItem extends ModuleFull {
  pricingCount: number;
  subscribedTenantCount: number;
  /** Record types (kartoteki) belonging to this module. */
  kartoteki?: ModuleKartotekaItem[];
  /** True when the module was built in the Kreator (vs a hardcoded system module). */
  isTemplateOnly?: boolean;
}

interface ModulesResponse {
  modules: ModuleListItem[];
  total: number;
}

type StatusKey = 'active' | 'inactive' | 'deleted';

/** Stabilny klucz statusu — accessor filtra select (exact-match, niezależny od tłumaczenia). */
function statusKey(row: ModuleListItem): StatusKey {
  if (row.gcrecord) return 'deleted';
  if (!row.isActive) return 'inactive';
  return 'active';
}

function statusBadge(row: ModuleListItem): { color: BadgeColor; label: string } {
  if (row.gcrecord) return { color: 'gray', label: 'admin.modules.status.deleted' };
  if (!row.isActive) return { color: 'amber', label: 'admin.modules.status.inactive' };
  return { color: 'green', label: 'admin.modules.status.active' };
}

export function AdminModules() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [editTarget, setEditTarget] = useState<ModuleListItem | null>(null);
  const [softDeleteTarget, setSoftDeleteTarget] = useState<ModuleListItem | null>(null);
  const [restoreTarget, setRestoreTarget] = useState<ModuleListItem | null>(null);
  const [showRetired, setShowRetired] = useState(false);

  const query = useQuery({
    queryKey: ['admin', 'modules', 'list', { showRetired }],
    queryFn: async (): Promise<ModulesResponse> => {
      const qs = showRetired
        ? '/admin/modules?detail=full&include_inactive=true&include_deleted=true'
        : '/admin/modules?detail=full&include_inactive=true';
      const res = await api.get<ModulesResponse>(qs);
      return res.data;
    },
  });

  const onMutationSuccess = () => {
    setSoftDeleteTarget(null);
    setRestoreTarget(null);
    void queryClient.invalidateQueries({ queryKey: ['admin', 'modules'] });
  };

  const columns = useMemo<ColumnDefinition<ModuleListItem>[]>(
    () => [
      {
        id: 'code',
        accessor: 'code',
        header: t('admin.modules.columns.code'),
        sortable: true,
        renderCell: (value) => <span className="font-mono text-xs">{String(value)}</span>,
      },
      {
        id: 'name',
        accessor: 'name',
        header: t('admin.modules.columns.name'),
        sortable: true,
        // Pochodzenie modułu/kartotek niesie teraz kolor chipów (KartotekaBadge w kolumnie
        // „Kartoteki"), więc osobny badge „Kreator" przy nazwie jest zbędny (spójnie z develop).
        renderCell: (_value, row) => (
          <span className="font-medium text-text-primary">{row.name}</span>
        ),
      },
      {
        id: 'description',
        header: t('admin.modules.columns.description'),
        accessor: (row) => row.description ?? '',
        sortable: false,
        filterable: false,
        defaultWidth: 320,
        // Opis zawijany (nie ucinany) — spójnie z kolumną „Opis" w Kartotekach.
        renderCell: (_value, row) =>
          row.description ? (
            <span className="block whitespace-normal break-words text-text-secondary">
              {row.description}
            </span>
          ) : (
            <span className="text-text-muted">—</span>
          ),
      },
      {
        id: 'area',
        accessor: (row) => row.areaName ?? t('admin.modules.groups.noArea'),
        header: t('admin.modules.columns.area'),
        sortable: true,
      },
      {
        id: 'kartoteki',
        accessor: (row) => (row.kartoteki ?? []).map((k) => k.name).join(', '),
        header: t('admin.modules.columns.kartoteki'),
        renderCell: (_value, row) =>
          row.kartoteki && row.kartoteki.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1">
              {row.kartoteki.map((k) => (
                <KartotekaBadge
                  key={k.slug}
                  kind={k.kind}
                  name={k.name}
                  data-testid={`module-kartoteka-chip-${row.code}-${k.slug}`}
                />
              ))}
            </div>
          ) : (
            <span className="text-text-muted">—</span>
          ),
      },
      {
        id: 'isCore',
        accessor: (row) => !!row.isCore,
        header: t('admin.modules.columns.isCore'),
        // Kolumna logiczna → filtr tri-state (Wszystkie/Tak/Nie), client-side.
        headerFilter: { category: 'boolean' },
        renderCell: (_value, row) =>
          row.isCore ? <Badge color="accent">{t('admin.modules.coreBadge')}</Badge> : null,
      },
      {
        id: 'sortOrder',
        accessor: (row) => row.sortOrder ?? '',
        header: t('admin.modules.columns.sortOrder'),
        sortable: true,
        // Liczba porządkowa — bez szukajki (fraza nie ma sensu).
        filterable: false,
        renderCell: (_value, row) => <span>{row.sortOrder ?? '—'}</span>,
      },
      {
        id: 'pricingCount',
        accessor: 'pricingCount',
        header: t('admin.modules.columns.pricingCount'),
        sortable: true,
        filterable: false,
      },
      {
        id: 'status',
        accessor: (row) => statusKey(row),
        header: t('admin.modules.columns.status'),
        // Stany rozłączne → dropdown wyboru (exact-match, bez zapytań).
        headerFilter: {
          category: 'select',
          options: [
            { value: 'active', label: t('admin.modules.status.active') },
            { value: 'inactive', label: t('admin.modules.status.inactive') },
            { value: 'deleted', label: t('admin.modules.status.deleted') },
          ],
        },
        renderCell: (_value, row) => {
          const { color, label } = statusBadge(row);
          return <Badge color={color}>{t(label)}</Badge>;
        },
      },
    ],
    [t],
  );

  const renderActions = (row: ModuleListItem) => {
    const isRetired = !!row.gcrecord;
    return (
      <div className="flex items-center justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={() => setEditTarget(row)}>
          {t('admin.modules.actions.edit')}
        </Button>
        {isRetired ? (
          <Button variant="ghost" size="sm" onClick={() => setRestoreTarget(row)}>
            {t('admin.modules.actions.restore')}
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSoftDeleteTarget(row)}
            disabled={row.isCore === true}
            title={
              row.isCore === true ? t('admin.modules.actions.softDeleteCoreTooltip') : undefined
            }
          >
            {t('admin.modules.actions.softDelete')}
          </Button>
        )}
      </div>
    );
  };

  return (
    <Layout title={t('admin.modules.title')}>
      <section className="px-6 py-8 flex flex-col gap-4">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-text-primary">{t('admin.modules.title')}</h2>
            <p className="mt-1 text-sm text-text-tertiary">{t('admin.modules.subtitle')}</p>
          </div>
          <div className="flex items-center gap-4">
            <Checkbox
              label={t('admin.modules.filters.showRetired')}
              checked={showRetired}
              onChange={(e) => setShowRetired(e.target.checked)}
            />
            <Button disabled title={t('admin.modules.addDisabledTooltip')}>
              {t('admin.modules.addButton')}
            </Button>
          </div>
        </div>

        {query.isLoading ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} height={48} />
            ))}
          </div>
        ) : query.isError ? (
          <div
            role="alert"
            className="rounded-xl border border-red-300 bg-red-50 p-6 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300"
          >
            <p>{t('admin.modules.loadError')}</p>
            <Button
              variant="secondary"
              size="sm"
              className="mt-3"
              onClick={() => void query.refetch()}
            >
              {t('admin.modules.retry')}
            </Button>
          </div>
        ) : (
          <PreviewTenantAuthBoundary>
            <ConfigurableTable
              tableId="admin-modules-catalog"
              columns={columns}
              data={query.data?.modules ?? []}
              getRowId={(row) => row.code}
              renderActions={renderActions}
              actionsColumnLabel={t('admin.modules.columns.actions')}
              persistConfig={false}
              showViewModeToggle={false}
              emptyIcon={Blocks}
              emptyTitle={t('admin.modules.empty')}
              emptyDescription=""
            />
          </PreviewTenantAuthBoundary>
        )}

        <EditModuleDrawer
          open={!!editTarget}
          module={editTarget}
          onClose={() => setEditTarget(null)}
          onSuccess={() => setEditTarget(null)}
        />

        {softDeleteTarget && (
          <AdminSoftDeleteModuleModal
            open
            moduleCode={softDeleteTarget.code}
            moduleName={softDeleteTarget.name}
            onClose={() => setSoftDeleteTarget(null)}
            onSuccess={onMutationSuccess}
          />
        )}

        {restoreTarget && (
          <AdminRestoreModuleModal
            open
            moduleCode={restoreTarget.code}
            moduleName={restoreTarget.name}
            onClose={() => setRestoreTarget(null)}
            onSuccess={onMutationSuccess}
          />
        )}
      </section>
    </Layout>
  );
}
